import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerOverlay,
    Spinner,
    Text,
} from "@chakra-ui/react";
import SubscriptionDetailHeader from "./SubscriptionDetailHeader";
import SubscriptionActions from "./SubscriptionActions";
import DetailContentTabs from "./DetailContentTabs";

const drawerContentProps = {
    background: "rgba(20, 20, 20, 0.96)",
    backdropFilter: "blur(20px)",
    borderLeft: "1px solid rgba(255, 255, 255, 0.12)",
    color: "rgba(255, 255, 255, 0.9)",
};

const drawerBodyProps = {
    p: 5,
    display: "flex",
    flexDirection: "column",
    gap: 5,
};

/**
 * Normalizes the API response into { sub, stripeState, history }.
 * Handles flat shape ({...sub, stripe_state, history}) and wrapped shape
 * ({subscription, stripe_state, history}).
 * @param {Object} data - Decrypted API payload
 * @returns {{ sub: Object, stripeState: Object|null, history: Array }}
 */
function normalizeDetail(data) {
    if (!data || typeof data !== "object") return { sub: null, stripeState: null, history: [] };
    const sub = data.subscription && typeof data.subscription === "object" ? data.subscription : data;
    const stripeState = data.stripe_state || sub?.stripe_state || null;
    const history = Array.isArray(data.history) ? data.history : (Array.isArray(sub?.history) ? sub.history : []);
    return { sub, stripeState, history };
}

/**
 * Fetches the subscription detail and re-fetches when the ID changes
 * or when the parent triggers a refresh.
 * @param {string|number|null} subscriptionId - Selected subscription ID
 * @returns {{ detail: Object, loading: boolean, error: string|null, refresh: Function }}
 */
function useSubscriptionDetail(subscriptionId) {
    const [detail, setDetail] = useState({ sub: null, stripeState: null, history: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    const fetchDetail = useCallback(async (id, signal) => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/admin/subscriptions/${id}`);
            if (!signal.aborted) setDetail(normalizeDetail(data));
        } catch (err) {
            if (!signal.aborted) setError(err?.response?.data || "Impossible de charger le détail");
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!subscriptionId) { setDetail({ sub: null, stripeState: null, history: [] }); return undefined; }
        const controller = new AbortController();
        fetchDetail(subscriptionId, controller.signal);
        return () => controller.abort();
    }, [subscriptionId, tick, fetchDetail]);

    return { detail, loading, error, refresh: () => setTick((t) => t + 1) };
}

/**
 * Renders a centered loading spinner inside the drawer body.
 * @returns {React.ReactElement}
 */
function DrawerLoading() {
    return (
        <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Spinner size="lg" color="#17dcb8" />
        </Box>
    );
}

/**
 * Renders an error message inside the drawer body.
 * @param {Object} props
 * @param {*} props.message - Error message (string or object)
 * @returns {React.ReactElement}
 */
function DrawerError({ message }) {
    return (
        <Text color="rgba(229, 115, 115, 1)" fontSize="0.85rem">
            {typeof message === "string" ? message : "Erreur lors du chargement"}
        </Text>
    );
}

/**
 * Composes the drawer body once detail is loaded: header, actions and sub-tabs.
 * @param {Object} props
 * @param {Object} props.detail - Normalized detail object
 * @param {Function} props.onClose - Drawer close callback
 * @param {Function} props.onChanged - Callback after a successful action
 * @returns {React.ReactElement}
 */
function DrawerBodyContent({ detail, onClose, onChanged }) {
    return (
        <>
            <SubscriptionDetailHeader sub={detail.sub} onClose={onClose} />
            <SubscriptionActions sub={detail.sub} onChanged={onChanged} />
            <DetailContentTabs subscriptionId={detail.sub?.id} detail={detail} />
        </>
    );
}

/**
 * Right-side drawer that shows a subscription's detail, history and actions.
 * @param {Object} props
 * @param {string|number|null} props.subscriptionId - Selected subscription ID (drives open state)
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onChanged - Called after Cancel or Reactivate succeeds (parent refresh)
 * @returns {React.ReactElement}
 */
function SubscriptionDetailDrawer({ subscriptionId, onClose, onChanged }) {
    const { detail, loading, error, refresh } = useSubscriptionDetail(subscriptionId);

    const handleChanged = () => { refresh(); onChanged?.(); };

    return (
        <Drawer isOpen={Boolean(subscriptionId)} onClose={onClose} placement="right" size="md">
            <DrawerOverlay />
            <DrawerContent {...drawerContentProps}>
                <DrawerBody {...drawerBodyProps}>
                    {loading && <DrawerLoading />}
                    {!loading && error && <DrawerError message={error} />}
                    {!loading && !error && detail.sub && (
                        <DrawerBodyContent detail={detail} onClose={onClose} onChanged={handleChanged} />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

export default SubscriptionDetailDrawer;
